import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Poll {
  id: string;
  title: string;
  description: string;
  options: Array<{
    id: string;
    text: string;
    votes: number;
  }>;
  createdBy: {
    id: string;
    name: string;
    avatar?: string;
  };
  createdAt: string;
  endDate?: string;
  status: 'active' | 'closed' | 'cancelled';
  totalVotes: number;
  userVote?: string; // Option ID that user voted for
  isAnonymous: boolean;
}

const ChamaPollsVoting: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    if (chamaId) {
      loadPolls();
    }
  }, [chamaId]);

  const loadPolls = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaPollsVoting: No auth token found');
        return;
      }

      const response = await fetch(API_ENDPOINTS.CHAMA_POLLS(chamaId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setPolls(data.data);
        } else {
          setPolls([]);
        }
      } else {
        console.error('❌ ChamaPollsVoting: Failed to load polls');
        setPolls([]);
      }
    } catch (error) {
      console.error('❌ ChamaPollsVoting: Error loading polls:', error);
      setPolls([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (pollId: string, optionId: string) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        alert('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.CHAMA_POLLS(chamaId)}/${pollId}/vote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ optionId }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('Vote submitted successfully!');
          loadPolls(); // Refresh polls data
        } else {
          alert(`Vote failed: ${data.error || 'Unknown error'}`);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Vote failed: ${errorData.error || 'Network error'}`);
      }
    } catch (error) {
      console.error('Vote error:', error);
      alert('Network error: Unable to submit vote. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return colors.primary;
      case 'closed': return colors.success;
      case 'cancelled': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return '🗳️';
      case 'closed': return '✅';
      case 'cancelled': return '❌';
      default: return '❓';
    }
  };

  const filteredPolls = polls.filter(poll => {
    if (activeTab === 'all') return true;
    return poll.status === activeTab;
  });

  const renderPollCard = (poll: Poll) => {
    const hasUserVoted = !!poll.userVote;
    const isActive = poll.status === 'active';
    const totalVotes = poll.totalVotes;

    return (
      <div key={poll.id} style={{ ...styles.pollCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div style={styles.pollHeader}>
          <div style={styles.pollCreator}>
            <div style={styles.creatorAvatar}>
              {poll.createdBy.avatar ? (
                <img src={poll.createdBy.avatar} alt={poll.createdBy.name} style={styles.avatarImage} />
              ) : (
                <span style={{ fontSize: '16px' }}>
                  {poll.createdBy.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              )}
            </div>
            <div style={styles.creatorInfo}>
              <div style={{ ...styles.creatorName, color: colors.text }}>{poll.createdBy.name}</div>
              <div style={{ ...styles.pollDate, color: colors.textSecondary }}>
                {new Date(poll.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
          <div style={{ ...styles.pollStatus, backgroundColor: getStatusColor(poll.status) + '20', border: `1px solid ${getStatusColor(poll.status)}` }}>
            <span style={{ fontSize: '16px', marginRight: '4px' }}>{getStatusIcon(poll.status)}</span>
            <span style={{ ...styles.statusText, color: getStatusColor(poll.status) }}>
              {poll.status.charAt(0).toUpperCase() + poll.status.slice(1)}
            </span>
          </div>
        </div>

        <div style={styles.pollContent}>
          <h3 style={{ ...styles.pollTitle, color: colors.text }}>{poll.title}</h3>
          <p style={{ ...styles.pollDescription, color: colors.textSecondary }}>{poll.description}</p>
        </div>

        {poll.endDate && (
          <div style={styles.pollDeadline}>
            <div style={{ ...styles.deadlineLabel, color: colors.textSecondary }}>Voting ends:</div>
            <div style={{ ...styles.deadlineDate, color: colors.text }}>
              {new Date(poll.endDate).toLocaleDateString()}
            </div>
          </div>
        )}

        <div style={styles.pollStats}>
          <div style={styles.pollStat}>
            <div style={{ ...styles.statValue, color: colors.text }}>{totalVotes}</div>
            <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Total Votes</div>
          </div>
          <div style={styles.pollStat}>
            <div style={{ ...styles.statValue, color: colors.text }}>{poll.options.length}</div>
            <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Options</div>
          </div>
          {hasUserVoted && (
            <div style={styles.pollStat}>
              <div style={{ ...styles.statValue, color: colors.success }}>✓</div>
              <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Voted</div>
            </div>
          )}
        </div>

        <div style={styles.pollOptions}>
          {poll.options.map((option) => {
            const votePercentage = totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0;
            const isUserChoice = poll.userVote === option.id;

            return (
              <div key={option.id} style={styles.optionContainer}>
                <div
                  style={{
                    ...styles.optionButton,
                    backgroundColor: isActive && !hasUserVoted ? colors.surface : 'transparent',
                    border: `2px solid ${isUserChoice ? colors.primary : hasUserVoted ? colors.border : colors.primary}`,
                    cursor: isActive && !hasUserVoted ? 'pointer' : 'default',
                    opacity: isActive && !hasUserVoted ? 1 : 0.7,
                  }}
                  onClick={() => {
                    if (isActive && !hasUserVoted) {
                      handleVote(poll.id, option.id);
                    }
                  }}
                >
                  <div style={styles.optionContent}>
                    <div style={{ ...styles.optionText, color: colors.text }}>{option.text}</div>
                    {hasUserVoted && (
                      <div style={styles.optionStats}>
                        <div style={styles.voteBar}>
                          <div
                            style={{
                              ...styles.voteFill,
                              width: `${votePercentage}%`,
                              backgroundColor: isUserChoice ? colors.primary : colors.secondary,
                            }}
                          />
                        </div>
                        <div style={{ ...styles.votePercentage, color: colors.textSecondary }}>
                          {option.votes} votes ({votePercentage.toFixed(1)}%)
                        </div>
                      </div>
                    )}
                  </div>
                  {isUserChoice && (
                    <div style={{ ...styles.userChoiceIndicator, backgroundColor: colors.primary }}>
                      <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {hasUserVoted && (
          <div style={{ ...styles.votedNotice, backgroundColor: colors.success + '20', border: `1px solid ${colors.success}` }}>
            <span style={{ color: colors.success }}>
              ✅ You have voted in this poll. Results are visible to all participants.
            </span>
          </div>
        )}

        {!isActive && (
          <div style={{ ...styles.closedNotice, backgroundColor: colors.warning + '20', border: `1px solid ${colors.warning}` }}>
            <span style={{ color: colors.warning }}>
              ⏰ This poll is {poll.status}. {poll.status === 'closed' ? 'Voting has ended.' : 'Voting was cancelled.'}
            </span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading polls & voting...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Polls & Voting"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Summary Cards */}
          <div style={styles.summaryGrid}>
            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>🗳️</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {polls.filter(p => p.status === 'active').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Active Polls</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>✅</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {polls.filter(p => p.status === 'closed').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Completed</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>📊</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {polls.reduce((sum, poll) => sum + poll.totalVotes, 0)}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Votes</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>👥</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {polls.length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Polls</div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div style={styles.tabContainer}>
            {[
              { id: 'all', label: 'All Polls', count: polls.length },
              { id: 'active', label: 'Active', count: polls.filter(p => p.status === 'active').length },
              { id: 'closed', label: 'Closed', count: polls.filter(p => p.status === 'closed').length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  ...styles.tabButton,
                  backgroundColor: activeTab === tab.id ? colors.primary : 'transparent',
                  color: activeTab === tab.id ? 'white' : colors.text,
                }}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Polls List */}
          <div style={styles.pollsList}>
            {filteredPolls.length > 0 ? (
              filteredPolls.map(renderPollCard)
            ) : (
              <div style={{ ...styles.emptyState, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                <div style={styles.emptyIcon}>🗳️</div>
                <div style={{ ...styles.emptyTitle, color: colors.text }}>No polls found</div>
                <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
                  {activeTab === 'all'
                    ? 'No polls have been created yet.'
                    : `No ${activeTab} polls found.`
                  }
                </div>
              </div>
            )}
          </div>

          {/* Create Poll Button */}
          <button style={{ ...styles.createButton, backgroundColor: colors.primary }}>
            Create New Poll
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  summaryCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    ...getShadowStyle('sm'),
  },
  summaryIcon: {
    fontSize: '24px',
    marginRight: spacing.md,
  },
  summaryInfo: {
    flex: 1,
  },
  summaryValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  tabContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  tabButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.md,
    border: 'none',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  pollsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  pollCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...getShadowStyle('sm'),
  },
  pollHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  pollCreator: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  creatorAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 20,
    objectFit: 'cover',
  },
  creatorInfo: {
    flex: 1,
  },
  creatorName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  pollDate: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  pollStatus: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.md,
    display: 'flex',
    alignItems: 'center',
  },
  statusText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  pollContent: {
    marginBottom: spacing.md,
  },
  pollTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
    marginBottom: spacing.sm,
  },
  pollDescription: {
    fontSize: typography.fontSize.base,
    lineHeight: 1.5,
    margin: 0,
  },
  pollDeadline: {
    marginBottom: spacing.md,
  },
  deadlineLabel: {
    fontSize: typography.fontSize.sm,
  },
  deadlineDate: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.xs / 2,
  },
  pollStats: {
    display: 'flex',
    gap: spacing.lg,
    marginBottom: spacing.md,
  },
  pollStat: {
    flex: 1,
  },
  statValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  pollOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  optionContainer: {
    // Container for option button
  },
  optionButton: {
    borderRadius: borderRadius.md,
    padding: spacing.md,
    position: 'relative',
  },
  optionContent: {
    flex: 1,
  },
  optionText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  optionStats: {
    // Container for vote stats
  },
  voteBar: {
    height: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 3,
    marginBottom: spacing.xs,
    overflow: 'hidden',
  },
  voteFill: {
    height: '100%',
    borderRadius: 3,
  },
  votePercentage: {
    fontSize: typography.fontSize.sm,
  },
  userChoiceIndicator: {
    position: 'absolute',
    top: spacing.sm,
    right: spacing.sm,
    width: 24,
    height: 24,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  votedNotice: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginTop: spacing.md,
  },
  closedNotice: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginTop: spacing.md,
  },
  emptyState: {
    textAlign: 'center',
    padding: spacing.xl,
    borderRadius: borderRadius.lg,
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
  },
  createButton: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    marginTop: spacing.lg,
  },
};

export default ChamaPollsVoting;